import { useEffect, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Send, Flame, GripVertical, Pencil, Check, X } from "lucide-react";
import { useBilling } from "../context/BillingContext";

export default function Prompts() {
  const { handleApiError } = useBilling();
  const [prompts, setPrompts] = useState([]);
  const [responses, setResponses] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [newCadence, setNewCadence] = useState("daily");
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ label: "", cadence: "daily" });
  const dragId = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/prompts"), api.get("/prompts/responses")]);
    setPrompts(a.data); setResponses(b.data);
  };
  useEffect(() => { load(); }, []);

  const addPrompt = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    try {
      await api.post("/prompts", { label: newLabel, cadence: newCadence });
      setNewLabel("");
      load();
    } catch (e) { if (!handleApiError(e)) toast.error("Add failed"); }
  };
  const removePrompt = async (id) => {
    try { await api.delete(`/prompts/${id}`); load(); }
    catch (e) { handleApiError(e); }
  };
  const startEdit = (p) => { setEditingId(p.id); setEditDraft({ label: p.label, cadence: p.cadence }); };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (id) => {
    if (!editDraft.label.trim()) return;
    try {
      await api.put(`/prompts/${id}`, editDraft);
      setEditingId(null);
      load();
    } catch (e) { if (!handleApiError(e)) toast.error("Save failed"); }
  };
  const respond = async (id) => {
    const v = (answers[id] || "").trim();
    if (!v) return;
    setBusy((b)=>({...b, [id]: true}));
    try {
      await api.post("/prompts/respond", { prompt_id: id, response: v });
      setAnswers((a)=>({...a, [id]: ""}));
      toast.success("Captured — that's a win on the board.");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy((b)=>({...b, [id]: false})); }
  };

  const onDragStart = (id) => (e) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    setDragOverId(id);
  };
  const onDrop = (id) => async (e) => {
    e.preventDefault();
    const from = dragId.current;
    dragId.current = null;
    setDragOverId(null);
    if (!from || from === id) return;
    const ids = prompts.map(p => p.id);
    const fromIdx = ids.indexOf(from);
    const toIdx = ids.indexOf(id);
    const [moved] = ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, moved);
    setPrompts((prev) => ids.map((pid, i) => ({ ...prev.find(p => p.id === pid), order: i })));
    try {
      await api.post("/prompts/reorder", { ids });
    } catch (e) {
      if (!handleApiError(e)) toast.error("Reorder failed");
      load();
    }
  };

  const dailyCount = responses.filter(r => r.created_at.slice(0,10) === new Date().toISOString().slice(0,10)).length;

  return (
    <AppShell eyebrow="Daily ritual" title="Reflection Prompts">
      <div className="border border-zinc-200 p-6 mb-8 brand-gradient-soft flex items-center gap-4" data-testid="streak-card">
        <Flame className="w-8 h-8 text-[#FF3B30]" />
        <div>
          <div className="text-2xl font-black tracking-tight">{dailyCount} reflection{dailyCount===1?"":"s"} today</div>
          <div className="text-sm text-zinc-700">Small wins compound. Each captured thought is fuel for your story.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        <div className="lg:col-span-2 bg-white p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#0033FF]">Today's questions</div>
            <div className="text-xs text-zinc-500 hidden sm:block">Drag <GripVertical className="w-3 h-3 inline -mt-0.5" /> to reorder · ✎ to edit</div>
          </div>

          {prompts.map((p) => (
            <div
              key={p.id}
              draggable={editingId !== p.id}
              data-draggable={editingId !== p.id ? "true" : "false"}
              onDragStart={onDragStart(p.id)}
              onDragOver={onDragOver(p.id)}
              onDrop={onDrop(p.id)}
              onDragEnd={() => setDragOverId(null)}
              className={`border border-zinc-200 p-4 bg-white ${dragOverId===p.id ? "drag-over" : ""}`}
              data-testid={`prompt-card-${p.id}`}
            >
              <div className="flex items-start gap-3">
                <GripVertical className="w-4 h-4 text-zinc-400 mt-1 shrink-0" data-testid={`prompt-handle-${p.id}`} />
                <div className="flex-1 min-w-0">
                  {editingId === p.id ? (
                    <div className="space-y-2">
                      <input data-testid={`prompt-edit-label-${p.id}`} autoFocus className="w-full px-3 py-2 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm"
                        value={editDraft.label} onChange={(e)=>setEditDraft({...editDraft, label: e.target.value})} />
                      <div className="flex items-center gap-2">
                        <select data-testid={`prompt-edit-cadence-${p.id}`} value={editDraft.cadence} onChange={(e)=>setEditDraft({...editDraft, cadence: e.target.value})}
                          className="px-3 py-2 border border-zinc-300 text-xs bg-white focus:outline-none focus:border-[#0033FF]">
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </select>
                        <button onClick={()=>saveEdit(p.id)} data-testid={`prompt-edit-save-${p.id}`}
                          className="bg-[#0033FF] text-white px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 inline-flex items-center gap-1">
                          <Check className="w-3 h-3" /> Save
                        </button>
                        <button onClick={cancelEdit} data-testid={`prompt-edit-cancel-${p.id}`}
                          className="border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100 inline-flex items-center gap-1">
                          <X className="w-3 h-3" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">{p.cadence}</div>
                      <div className="text-base sm:text-lg font-semibold tracking-tight">{p.label}</div>
                    </>
                  )}
                </div>
                {editingId !== p.id && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={()=>startEdit(p)} data-testid={`prompt-edit-${p.id}`} className="text-zinc-400 hover:text-[#0033FF] p-1" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={()=>removePrompt(p.id)} data-testid={`prompt-delete-${p.id}`} className="text-zinc-400 hover:text-[#FF3B30] p-1" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {editingId !== p.id && (
                <div className="flex flex-col sm:flex-row gap-2 mt-3 sm:ml-7">
                  <input data-testid={`prompt-answer-${p.id}`} className="flex-1 px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm"
                    placeholder="A thought, a moment, a story…"
                    value={answers[p.id] || ""}
                    onChange={(e)=>setAnswers((a)=>({...a, [p.id]: e.target.value}))}
                    onKeyDown={(e)=>{ if (e.key === "Enter") respond(p.id); }}/>
                  <button onClick={()=>respond(p.id)} disabled={busy[p.id]} data-testid={`prompt-submit-${p.id}`}
                    className="bg-[#0033FF] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-50 inline-flex items-center justify-center gap-2 sm:w-auto">
                    <Send className="w-3 h-3" /> Save
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white p-6">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-3">Add a prompt</div>
          <form onSubmit={addPrompt} className="space-y-3">
            <input data-testid="new-prompt-label" className="w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm"
              placeholder="Your prompt question" value={newLabel} onChange={(e)=>setNewLabel(e.target.value)} />
            <select data-testid="new-prompt-cadence" value={newCadence} onChange={(e)=>setNewCadence(e.target.value)} className="w-full px-3 py-2.5 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#0033FF]">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
            <button type="submit" data-testid="new-prompt-add" className="w-full border border-zinc-900 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white inline-flex items-center justify-center gap-2">
              <Plus className="w-3 h-3" /> Add prompt
            </button>
          </form>

          <div className="mt-8">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-zinc-500 mb-3">Last reflections</div>
            <div className="divide-y divide-zinc-200 border-t border-zinc-200 max-h-[260px] overflow-y-auto">
              {responses.slice(0,8).map((r) => (
                <div key={r.id} className="py-3 text-sm">
                  <div className="text-[10px] tracking-wider text-zinc-500 font-mono">{new Date(r.created_at).toLocaleString()}</div>
                  <div className="text-zinc-700 line-clamp-2 mt-1">{r.response}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
